import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { API_BASE_URL, getAuthHeader } from '../../../utils/api';

// Configure axios instance
export const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true
});

// Add auth header interceptor
api.interceptors.request.use(async (config) => {
  const headers = await getAuthHeader(config.method);
  config.headers = { ...config.headers, ...Object.fromEntries(headers.entries()) };
  return config;
});

const AUCTIONS_KEY = 'auctions';

export const useAuctions = () => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: [AUCTIONS_KEY],
    queryFn: async () => {
      const { data } = await api.get('/auctions');
      
      // Transform auctions
      return data.map(auction => ({
        ...auction,
        is_system_auction: false,
        _auction_house_name: auction.auction_house_name,
        auction_house_name: auction.auction_house_name
      }));
    },
    initialData: undefined, // Don't use cached data as initial data
    staleTime: 30000, // Consider data fresh for 30 seconds
    cacheTime: 5 * 60 * 1000, // Cache for 5 minutes only
    retry: 3, // Retry failed requests up to 3 times
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
    refetchOnMount: 'if-stale', // Only refetch if data is stale
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnReconnect: true, // Refetch on reconnection
    onError: (error) => {
      console.error('Failed to fetch auctions:', error);
    }
  });
};

export const useScrapeAuctionLots = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ auctionId, lotNumbers, onProgress, signal }) => {
      const response = await api.post(
        `/auctions/${auctionId}/scrape-lots`,
        { lotNumbers },
        {
          responseType: 'text',
          signal,
          onDownloadProgress: (progressEvent) => {
            const text = progressEvent.event.target.responseText;
            // Get all new chunks since last update
            const chunks = text.split('\n\n').filter(Boolean);
            const newChunks = chunks.slice(-1); // Get only the newest chunk
            
            for (const chunk of newChunks) {
              try {
                const data = JSON.parse(chunk.replace('data: ', ''));
                if (data.logs) {
                  onProgress(data.logs);
                }
                if (data.complete) {
                  return { complete: true, count: data.count };
                }
              } catch (e) {
                console.error('Failed to parse SSE chunk:', e);
              }
            }
          }
        }
      );
      
      return { complete: true, count: 0 };
    },
    onSuccess: async (data, { auctionId }) => {
      // Invalidate and refetch relevant queries
      await queryClient.invalidateQueries({ queryKey: [AUCTIONS_KEY] });
      await queryClient.invalidateQueries({ queryKey: [AUCTIONS_KEY, auctionId] });
      await queryClient.refetchQueries({ queryKey: [AUCTIONS_KEY] });
      await queryClient.refetchQueries({ queryKey: [AUCTIONS_KEY, auctionId] });
      return data;
    }
  });
};

export const useAuction = (id) => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: [AUCTIONS_KEY, id],
    queryFn: async () => {
      if (!id) {
        throw new Error('Auction ID is required');
      }

      // Check if this is a system auction by looking for the prefix
      const isSystemAuction = id.toString().startsWith('system_');
      
      if (isSystemAuction) {
        const systemId = id.replace('system_', '');
        const { data } = await api.get(`/auctions/system/${systemId}`);
        return {
          ...data,
          id: `system_${data.id}`,
          is_system_auction: true,
          start_date: data.date,
          won_lots: data.sold_lots,
          lost_lots: data.unsold_lots,
          system_auction_status: data.status,
          lots: data.lots?.map(lot => ({
            ...lot,
            status: lot.user_selection?.status || lot.status
          })),
          auction_timezone: data.timezone,
          user_timezone: data.user_timezone
        };
      }

      const { data } = await api.get(`/auctions/${id}`);
      return {
        ...data,
        is_system_auction: false
      };
    },
    enabled: !!id && !isNaN(parseInt(id)),
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnMount: 'if-stale', // Only refetch if data is stale
    staleTime: 30000, // Consider data fresh for 30 seconds
    // Don't use initialData from auctions list since it doesn't include lots
    initialData: undefined
  });
};

export const useCreateAuction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (auction) => {
      // Get all dates in range
      const getDatesInRange = (startDate, endDate) => {
        const dates = [];
        const start = new Date(startDate);
        const end = endDate ? new Date(endDate) : new Date(startDate);
        
        for (let date = new Date(start); date <= end; date.setDate(date.getDate() + 1)) {
          dates.push(new Date(date).toISOString().split('T')[0]);
        }
        
        return dates;
      };

      // Check if all dates have times set
      const dates = getDatesInRange(auction.start_date, auction.end_date);
      const missingTimes = dates.some(date => !auction.daily_times[date]);
      if (missingTimes) {
        throw new Error('Please set times for all auction dates');
      }

      const newAuction = {
        ...auction,
        end_date: auction.end_date || null
      };
      
      const { data } = await api.post('/auctions', newAuction);
      return data;
    },
    onSuccess: async (newAuction) => {
      // Invalidate and force refetch to ensure fresh data
      await queryClient.invalidateQueries({ queryKey: [AUCTIONS_KEY] });
      await queryClient.refetchQueries({ queryKey: [AUCTIONS_KEY] });
    },
    onMutate: async (newAuction) => {
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: [AUCTIONS_KEY] });

      // Snapshot the previous value
      const previousAuctions = queryClient.getQueryData([AUCTIONS_KEY]);

      // Optimistically update to the new value
      if (previousAuctions) {
        queryClient.setQueryData([AUCTIONS_KEY], [...previousAuctions, { ...newAuction, id: Date.now() }]);
      }

      return { previousAuctions };
    },
    onError: (err, newAuction, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousAuctions) {
        queryClient.setQueryData([AUCTIONS_KEY], context.previousAuctions);
      }
    }
  });
};

export const useCreateLot = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ auctionId, lot }) => {
      const { data } = await api.post(`/auctions/${auctionId}/lots`, lot);
      return data;
    },
    onSuccess: async (_, { auctionId }) => {
      await queryClient.invalidateQueries({ queryKey: [AUCTIONS_KEY, auctionId] });
      await queryClient.refetchQueries({ queryKey: [AUCTIONS_KEY, auctionId] });
    }
  });
};

export const useUpdateLot = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ auctionId, lotId, lot }) => {
      const { data } = await api.put(`/auctions/${auctionId}/lots/${lotId}`, lot);
      return data;
    },
    onSuccess: async (_, { auctionId }) => {
      await queryClient.invalidateQueries({ queryKey: [AUCTIONS_KEY, auctionId] });
      await queryClient.refetchQueries({ queryKey: [AUCTIONS_KEY, auctionId] });
    }
  });
};

export const useDeleteLot = () => {
  return useMutation({
    mutationFn: async ({ auctionId, lotId }) => {
      await api.delete(`/auctions/${auctionId}/lots/${lotId}`);
    },
  });
};

export const useUpdateAuction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...auction }) => {
      // Get all dates in range
      const getDatesInRange = (startDate, endDate) => {
        const dates = [];
        const start = new Date(startDate);
        const end = endDate ? new Date(endDate) : new Date(startDate);
        
        for (let date = new Date(start); date <= end; date.setDate(date.getDate() + 1)) {
          dates.push(new Date(date).toISOString().split('T')[0]);
        }
        
        return dates;
      };

      // Check if all dates have times set
      const dates = getDatesInRange(auction.start_date, auction.end_date);
      const missingTimes = dates.some(date => !auction.daily_times[date]);
      if (missingTimes) {
        throw new Error('Please set times for all auction dates');
      }

      const updatedAuction = {
        ...auction,
        end_date: auction.end_date || null
      };

      const { data } = await api.put(`/auctions/${id}`, updatedAuction);
      return data;
    },
    onMutate: async (updatedAuction) => {
      await queryClient.cancelQueries({ queryKey: [AUCTIONS_KEY, updatedAuction.id] });
      const previousAuction = queryClient.getQueryData([AUCTIONS_KEY, updatedAuction.id]);
      queryClient.setQueryData([AUCTIONS_KEY, updatedAuction.id], updatedAuction);
      return { previousAuction, updatedAuction };
    },
    onError: (err, updatedAuction, context) => {
      queryClient.setQueryData([AUCTIONS_KEY, context.updatedAuction.id], context.previousAuction);
    },
    onSuccess: (data, { id }) => {
      queryClient.invalidateQueries({ queryKey: [AUCTIONS_KEY] });
      queryClient.setQueryData([AUCTIONS_KEY, id], data);
    },
  });
};

export const useDeleteAuction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      await api.delete(`/auctions/${id}`);
    },
    onSuccess: async (_, id) => {
      // Navigate away first, then invalidate queries to avoid 404
      await queryClient.invalidateQueries({ queryKey: [AUCTIONS_KEY] });
      queryClient.removeQueries({ queryKey: [AUCTIONS_KEY, id] });
    }
  });
};

export const useFetchHammerPrices = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (auctionId) => {
      const response = await api.post(`/auctions/${auctionId}/fetch-hammer-prices`);
      return response.data;
    },
    onSuccess: async (_, auctionId) => {
      // Invalidate and refetch relevant queries
      await queryClient.invalidateQueries({ queryKey: [AUCTIONS_KEY] });
      await queryClient.invalidateQueries({ queryKey: [AUCTIONS_KEY, auctionId] });
      await queryClient.refetchQueries({ queryKey: [AUCTIONS_KEY] });
      await queryClient.refetchQueries({ queryKey: [AUCTIONS_KEY, auctionId] });
    }
  });
};

export const useImportLots = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ auctionId, data }) => {
      const response = await api.post(`/auctions/${auctionId}/import-lots`, data);
      return response.data;
    },
    onSuccess: async (response, { auctionId }) => {
      // Invalidate and refetch relevant queries
      await queryClient.invalidateQueries({ queryKey: [AUCTIONS_KEY] });
      await queryClient.invalidateQueries({ queryKey: [AUCTIONS_KEY, auctionId] });
      await queryClient.invalidateQueries({ queryKey: ['coins'] });
      await queryClient.invalidateQueries({ queryKey: ['transactions'] });
      
      // Force refetch to ensure fresh data
      await queryClient.refetchQueries({ queryKey: [AUCTIONS_KEY] });
      await queryClient.refetchQueries({ queryKey: [AUCTIONS_KEY, auctionId] });
      await queryClient.refetchQueries({ queryKey: ['coins'] });
      await queryClient.refetchQueries({ queryKey: ['transactions'] });
      
      return response; // Return response so component can access created coin IDs
    }
  });
};
